import { Shape, ShapeType } from '@/services/shape';
import {
  addTansparentBorder,
  clipImg,
  resizeImg
} from '@/utils/transform-image';
import { ElectronApi } from '@/services/electron.api';
import { useLayerStore } from '@/store/use-layer-store';
import { dataURItoBlob } from '@/utils/fs/image.utils';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { useWatchfaceScreenShot } from './use-watchface-screenshot';
import { getActuallyWidgetSize } from '@/utils/shape/widget.size.utils';

const _borderConfighMap: Record<
  string,
  {
    width: number;
    offset: number;
  }
> = {
  N65: {
    width: 6,
    offset: 6
  },
  N62: {
    width: 3,
    offset: 0
  },
  O62: {
    width: 3,
    offset: 0
  },
  N62S: {
    width: 3,
    offset: 0
  },
  M67: {
    width: 3,
    offset: 3
  }
};

export const useSlotPreview = () => {
  const { screenshot } = useWatchfaceScreenShot();

  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();

  /**
   * 从 base64 图片中找到非透明像素的包围盒
   */
  const _getContentBounds = async (dataUrl: string) => {
    const img = new Image();
    await new Promise<void>((res) => {
      img.addEventListener('load', () => res());
      img.src = dataUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data, width, height } = imageData;

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (maxX < minX || maxY < minY) return; // 完全透明
    return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  };

  const _getBorderConfig = () => {
    return (
      _borderConfighMap[watchfaceStore.deviceType ?? ''] ?? {
        width: 3,
        offset: 0
      }
    );
  };

  const generateWidgetPreviewIfNeed = async () => {
    const themes = layerStore.themes;
    // 获取缩放比例，当widget在编辑状态时需要缩放
    for (const theme of themes) {
      const stack: {
        layer: StoreLayerItem;
        parent: StoreLayerItem | undefined;
      }[] = theme.children.map((item) => {
        return {
          layer: item,
          parent: undefined
        };
      });
      while (stack.length) {
        const { layer, parent } = stack.shift() ?? {};
        if (layer?.children) {
          stack.push(
            ...(layer?.children?.map((item) => {
              return {
                layer: item,
                parent: layer
              };
            }) ?? [])
          );
        }

        const shape = Shape.getShape(layer?.id ?? '', layer);
        if (!shape || !layer) continue;

        if (layer?.type !== ShapeType.Widget) continue;
        if (parent?.type !== ShapeType.Slot) continue;

        for (const colorItem of theme.colorGroupTable) {
          const res = shape.getResources({
            resourceName: 'widgetPreview',
            colorGroup: colorItem.id
          });

          if (!res.length) {
            await generateSingleWidgetPreview({
              theme,
              colorId: colorItem.id,
              widgetId: shape.id
            });
          }
        }
      }
    }
  };

  /**
   * 生成widget预览图
   */
  const generateSingleWidgetPreview = async (data: {
    theme: StoreThemeItem;
    widgetId: string;
    colorId: string;
  }) => {
    const fs = ElectronApi.fs;
    const path = ElectronApi.path;
    if (!fs || !path) return;
    const { theme, widgetId, colorId } = data;
    const shape = Shape.getShape(widgetId);
    if (shape?.type !== ShapeType.Widget) return;

    const slot = shape.parent;
    if (slot?.type !== ShapeType.Slot) return;

    // 生成预览图
    const {
      contentW: widgetActuallyWidth = 0,
      contentH: widgetActuallyHeight = 0
    } = getActuallyWidgetSize(shape);

    // 获取widget子元素围成的区域大小
    if (!widgetActuallyWidth || !widgetActuallyHeight) return;

    // 获取预览图缩放比例
    const { width: editBoxWidth, height: editBoxHeight } =
      (slot.attrs.sameEditBox
        ? slot.getImageSize('editBox', colorId)
        : shape.getImageSize('editBox', colorId)) ?? {};
    if (!editBoxWidth || !editBoxHeight) return;

    const { width: borderWidth, offset: borderOffset } = _getBorderConfig();

    const widgetPreviewContentWidth = editBoxWidth - borderWidth * 2;
    const widgetPreviewContentHeight = editBoxHeight - borderWidth * 2;
    const scale = Math.min(
      widgetPreviewContentWidth / widgetActuallyWidth,
      widgetPreviewContentHeight / widgetActuallyHeight
    );
    const _widgetActuallyWidth = widgetPreviewContentWidth / scale;
    const _widgetActuallyHeight = widgetPreviewContentHeight / scale;

    // 开始截图
    const colorItem = theme.colorGroupTable.find((item) => item.id == colorId);
    if (!colorItem) return;
    const image = await screenshot(undefined, {
      themeName: theme?.name,
      themeType: theme?.type,
      shapeIds: [shape.id],
      themeColor: colorItem.color,
      ignoreBg: true,
      slotDisplayAll: true
    });
    if (!image) return;

    // 从截图中找到非透明像素的包围盒，确定内容的实际渲染位置
    const contentBounds = await _getContentBounds(image as string);
    if (!contentBounds) return;

    // 以内容包围盒中心为裁剪中心，确保内容在预览图中居中
    const contentCenterX = contentBounds.x + contentBounds.width / 2;
    const contentCenterY = contentBounds.y + contentBounds.height / 2;
    const clipX = contentCenterX - _widgetActuallyWidth / 2;
    const clipY = contentCenterY - _widgetActuallyHeight / 2;

    let res = await clipImg(image as string, {
      x: clipX,
      y: clipY,
      width: _widgetActuallyWidth,
      height: _widgetActuallyHeight
    });
    // 缩放预览图
    if (widgetPreviewContentWidth != _widgetActuallyWidth) {
      res = await resizeImg(res, {
        width: widgetPreviewContentWidth,
        height: widgetPreviewContentHeight
      });
    }
    // 如果预览图从editBox的0,0开始绘制，需要增加空白像素
    if (borderOffset == 0 && borderWidth) {
      res = await addTansparentBorder(res ?? '', {
        left: borderWidth,
        right: borderWidth,
        top: borderWidth,
        bottom: borderWidth
      });
    }
    if (!res) return;

    // 将预览图保存
    const imgPath = path.join(
      await ElectronApi.getPath('temp'),
      'temp_widgetPreview_' + widgetId + '.png'
    );
    const blob = dataURItoBlob(res);
    const arrBf = await blob.arrayBuffer();
    await fs.promises.writeFile(
      imgPath,
      Buffer.from(arrBf) as unknown as string
    );
    await shape.uploadResource({
      resourceName: 'widgetPreview',
      colorGroup: colorItem.id,
      realpath: imgPath,
      dirname: '_widget'
    });
    // 删除原文件
    await fs.promises.unlink(imgPath);
  };

  return {
    // generateAllWidgetsPreview,
    generateWidgetPreviewIfNeed,
    generateSingleWidgetPreview
  };
};
